// Typed wrappers around the /v1/admin/* endpoints. One function per call so the
// pages never build URLs by hand.
import { get, post, patch, put, del } from './session';
import type {
  AdminAccount,
  AdminRole,
  BotDeck,
  BotRow,
  CatalogCard,
  ContactMessageDetail,
  ContactMessageRow,
  DashboardStats,
  OwnedCard,
  UserDetail,
  UserRow,
} from '../types/admin';

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

// --- Dashboard --------------------------------------------------------------

export function getStats(fresh = false): Promise<DashboardStats> {
  return get<DashboardStats>(`/v1/admin/stats${fresh ? '?fresh=1' : ''}`);
}

// --- Users (read-only) ------------------------------------------------------

export async function listUsers(
  cursor: string | null,
  q: string,
  limit = 30,
): Promise<Page<UserRow>> {
  const body = await get<{ users: UserRow[]; nextCursor: string | null }>(
    `/v1/admin/users${qs({ cursor, q, limit })}`,
  );
  return { items: body.users, nextCursor: body.nextCursor };
}

/** A single player's profile, stats, recent matches, cards and decks. */
export function getUser(id: string): Promise<UserDetail> {
  return get<UserDetail>(`/v1/admin/users/${encodeURIComponent(id)}`);
}

// --- Bots -------------------------------------------------------------------

export async function listBots(
  cursor: string | null,
  q: string,
  limit = 30,
): Promise<Page<BotRow>> {
  const body = await get<{ bots: BotRow[]; nextCursor: string | null }>(
    `/v1/admin/bots${qs({ cursor, q, limit })}`,
  );
  return { items: body.bots, nextCursor: body.nextCursor };
}

export interface BotDetail {
  bot: BotRow;
  cards: OwnedCard[];
  decks: BotDeck[];
  degraded: string[];
  /** Upstream failure reason per degraded field, for diagnosing a 200 that is
   *  missing data (the BFF degrades rather than erroring). */
  degradedReasons?: Record<string, string>;
}

export function getBot(id: string): Promise<BotDetail> {
  return get<BotDetail>(`/v1/admin/bots/${encodeURIComponent(id)}`);
}

export interface CreateBotInput {
  userName: string;
  displayName: string;
  level: number;
  botPersonality?: string;
}

export function createBot(input: CreateBotInput): Promise<{ bot: BotRow }> {
  return post<{ bot: BotRow }>('/v1/admin/bots', input);
}

export interface UpdateBotInput {
  userName?: string;
  displayName?: string;
  level?: number;
  botPersonality?: string;
}

export function updateBot(id: string, input: UpdateBotInput): Promise<{ bot: BotRow }> {
  return patch<{ bot: BotRow }>(`/v1/admin/bots/${encodeURIComponent(id)}`, input);
}

export function deleteBot(id: string): Promise<unknown> {
  return del(`/v1/admin/bots/${encodeURIComponent(id)}`);
}

export function uploadBotPicture(id: string, imageData: string): Promise<{ bot: BotRow }> {
  return post<{ bot: BotRow }>(`/v1/admin/bots/${encodeURIComponent(id)}/picture`, { imageData });
}

export async function listBotCards(id: string): Promise<OwnedCard[]> {
  const body = await get<{ cards: OwnedCard[] }>(`/v1/admin/bots/${encodeURIComponent(id)}/cards`);
  return body.cards;
}

export function grantBotCards(id: string, cardIds: string[]): Promise<unknown> {
  return post(`/v1/admin/bots/${encodeURIComponent(id)}/cards`, { cardIds });
}

export function revokeBotCards(id: string, instanceIds: string[]): Promise<unknown> {
  return del(`/v1/admin/bots/${encodeURIComponent(id)}/cards`, { instanceIds });
}

export async function listBotDecks(id: string): Promise<BotDeck[]> {
  const body = await get<{ decks: BotDeck[] }>(`/v1/admin/bots/${encodeURIComponent(id)}/decks`);
  return body.decks;
}

export function updateBotDeck(
  id: string,
  deckId: string,
  name: string,
  instanceIds: string[],
): Promise<unknown> {
  return put(
    `/v1/admin/bots/${encodeURIComponent(id)}/decks/${encodeURIComponent(deckId)}`,
    { name, instanceIds },
  );
}

export function setBotActiveDeck(id: string, deckId: string): Promise<unknown> {
  return post(
    `/v1/admin/bots/${encodeURIComponent(id)}/decks/${encodeURIComponent(deckId)}/active`,
  );
}

// --- Catalog ----------------------------------------------------------------

export async function getCatalog(): Promise<CatalogCard[]> {
  const body = await get<{ cards: CatalogCard[] }>('/v1/admin/catalog');
  return body.cards;
}

// --- Contact messages -------------------------------------------------------

export async function listMessages(
  cursor: string | null,
  q: string,
  limit = 50,
): Promise<Page<ContactMessageRow>> {
  const body = await get<{ messages: ContactMessageRow[]; nextCursor: string | null }>(
    `/v1/admin/messages${qs({ cursor, q, limit })}`,
  );
  return { items: body.messages, nextCursor: body.nextCursor };
}

export async function getMessage(id: string): Promise<ContactMessageDetail> {
  const body = await get<{ message: ContactMessageDetail }>(
    `/v1/admin/messages/${encodeURIComponent(id)}`,
  );
  return body.message;
}

export async function markMessageRead(
  id: string,
  read: boolean,
): Promise<ContactMessageDetail> {
  const body = await patch<{ message: ContactMessageDetail }>(
    `/v1/admin/messages/${encodeURIComponent(id)}`,
    { read },
  );
  return body.message;
}

// --- Admin accounts (super admin only) --------------------------------------

export async function listAdmins(): Promise<AdminAccount[]> {
  const body = await get<{ admins: AdminAccount[] }>('/v1/admin/admins');
  return body.admins;
}

export function createAdmin(input: {
  username: string;
  displayName: string;
  role: AdminRole;
}): Promise<{ admin: AdminAccount; temporaryPassword?: string }> {
  return post('/v1/admin/admins', input);
}

export function updateAdmin(
  id: string,
  input: { displayName?: string; role?: AdminRole; isActive?: boolean },
): Promise<{ admin: AdminAccount }> {
  return patch(`/v1/admin/admins/${encodeURIComponent(id)}`, input);
}

export function deleteAdmin(id: string): Promise<unknown> {
  return del(`/v1/admin/admins/${encodeURIComponent(id)}`);
}

export function resetAdminPassword(id: string): Promise<{ temporaryPassword: string }> {
  return post(`/v1/admin/admins/${encodeURIComponent(id)}/reset-password`);
}
